import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Dna,
  Flask,
  Heartbeat,
  Brain,
  Calculator,
  Sparkle,
  ChartLine
} from '@phosphor-icons/react';

const predictionMessages = [
  { text: "Analyzing biomarker patterns...", icon: Dna },
  { text: "Evaluating hormonal cascade...", icon: Flask },
  { text: "Processing lifestyle modifiers...", icon: Heartbeat },
  { text: "Running predictive algorithm...", icon: Brain },
  { text: "Calculating confidence intervals...", icon: Calculator },
  { text: "Generating clinical insights...", icon: Sparkle },
  { text: "Finalizing analysis...", icon: ChartLine }
];

export const PredictionLoadingOverlay = () => {
  const [messageIndex, setMessageIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % predictionMessages.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);
  
  const currentMessage = predictionMessages[messageIndex];
  const IconComponent = currentMessage.icon;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F172A]/90 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full mx-4 shadow-2xl"
      >
        <div className="text-center">
          {/* DNA Helix Animation */}
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-[#0F172A]/10 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-[#5B21B6] rounded-full animate-spin" />
            <div className="absolute inset-2 border-4 border-transparent border-t-[#EC4899] rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
            <motion.div 
              key={messageIndex}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute inset-4 rounded-full bg-gradient-to-br from-[#5B21B6] to-[#EC4899] flex items-center justify-center"
            >
              <IconComponent className="w-8 h-8 text-white" weight="duotone" />
            </motion.div>
          </div>
          
          <motion.p 
            key={`msg-${messageIndex}`}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lg font-semibold text-[#0F172A] mb-2"
          >
            {currentMessage.text}
          </motion.p>
          
          <p className="text-sm text-[#64748B]">
            Performing comprehensive analysis
          </p>
          
          {/* Progress indicator */}
          <div className="flex justify-center gap-1.5 mt-6">
            {predictionMessages.map((_, index) => (
              <div 
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === messageIndex ? 'w-8 bg-[#5B21B6]' : 
                  index < messageIndex ? 'w-2 bg-[#5B21B6]' : 'w-2 bg-[#E2E8F0]'
                }`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
